import { useRef, useState, type DragEvent, type ReactNode } from 'react';

type Props = {
  onFiles: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
  // 'plain' (default) keeps the original dropzone byte-for-byte for existing
  // callers. 'full' = the redesigned empty-state card; 'compact' = the slim
  // "add another file" row shown once files exist.
  variant?: 'plain' | 'full' | 'compact';
};

// The dropzone is a <label> wrapping a hidden file input, so a click anywhere
// in it opens the picker via the browser's native label→control behaviour. An
// earlier version used a <div> with onClick={() => inputRef.current.click()};
// that programmatic .click() on a display:none input was unreliable and often
// produced no `change` event at all, so picking a file did nothing.
export function FileDrop({
  onFiles,
  accept,
  multiple = true,
  disabled = false,
  hint,
  variant = 'plain',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
  }

  const dragProps = {
    onDragOver: (e: DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setOver(true);
    },
    onDragLeave: () => setOver(false),
    onDrop,
  };

  const input = (
    <input
      ref={inputRef}
      type="file"
      className="hidden"
      accept={accept}
      multiple={multiple}
      onChange={(e) => {
        if (e.target.files?.length) onFiles(e.target.files);
        if (inputRef.current) inputRef.current.value = '';
      }}
      disabled={disabled}
    />
  );

  if (variant === 'compact') {
    return (
      <label
        {...dragProps}
        className={`flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-dashed px-3 py-2.5 text-sm font-semibold transition ${
          over ? 'border-[#4D148C] bg-[#F3EBFA] text-[#4D148C]' : 'border-slate-300 bg-slate-50 text-[#4D148C]'
        } ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-slate-100'}`}
      >
        {input}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>
          {hint ?? (
            <>
              Add another file <span className="font-medium text-slate-400">or drop here</span>
            </>
          )}
        </span>
      </label>
    );
  }

  if (variant === 'full') {
    return (
      <label
        {...dragProps}
        className={`flex flex-col items-center gap-2.5 rounded-xl border-[1.5px] border-dashed p-6 text-center transition ${
          over ? 'border-[#4D148C] bg-[#F3EBFA]' : 'border-slate-300 bg-slate-50'
        } ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-slate-100'}`}
      >
        {input}
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F3EBFA] text-[#4D148C]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 15V4" />
            <path d="M7.5 8.5 12 4l4.5 4.5" />
            <path d="M5 15v3.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V15" />
          </svg>
        </span>
        <span className="text-sm font-semibold text-slate-700">
          Drop file{multiple ? 's' : ''} here or <span className="text-[#4D148C]">click to browse</span>
        </span>
        <span className="text-xs text-slate-400">{hint ?? 'PDF, image, or Office document'}</span>
      </label>
    );
  }

  // variant === 'plain' — unchanged original rendering.
  return (
    <label
      {...dragProps}
      className={`block rounded-xl border-2 border-dashed p-6 text-center transition ${
        over ? 'border-[#4D148C] bg-[#F3EBFA]' : 'border-slate-200 bg-white'
      } ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}`}
    >
      {input}
      <p className="text-sm font-medium text-slate-700">
        Drop file{multiple ? 's' : ''} here or click to browse
      </p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </label>
  );
}
